import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import Button from '@repo/ui/button'
import { DeactivateFileIcon, EyeOpenIcon, NotebookIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

import { getHowDidYouHearAboutUs } from '../../../../api/how-did-you-hear-about-us/get-how-did-you-hear-about-us'
import { getPcrs } from '../../../../api/pcr/get-pcrs'
import { getStaffApplicationCurrentScoreCard } from '../../../../api/staff-application/get-staff-application-current-score-card'
import { Paths } from '../../../../lib/constants/path'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import {
  DeactivationReasonEnum,
  deactivationReasonEnumMapping
} from '../../../../lib/types/enums/staff/deactivation-reason.enum'
import {
  EvalutationEnum,
  evalutationEnumMapping
} from '../../../../lib/types/enums/staff-application/evalutation.enum'
import { AdminStaffInput } from '../../../../lib/types/models/staff/admin-staff-input'
import { getSelectedOption } from '../../../../lib/utils/functions/misc.functions'
import { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledDateStringInput from '../../../elements/inputs/controlled-date-string-input'
import ControlledRadioInput from '../../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import { PcrScoreCardModal } from '../../pcr/pcr-score-card-form/pcr-score-card-form'
import { mapScoreCardToInput } from '../../staff-application/utils'
import { StaffFormCommonProps } from '../utils/accordion-items'

const StaffRecrutementForm: React.FC<StaffFormCommonProps> = ({ mode }) => {
  const form = useFormContext<AdminStaffInput>()
  const staffApplicationId = form.getValues('applicationId')
  const { modalState, toggleModal, closeModel } = useModalManager(['staff-evaluation'])

  const { data, isPending } = useQuery({
    queryKey: ['staff-evaluation', staffApplicationId],
    queryFn: () => {
      if (staffApplicationId) return getStaffApplicationCurrentScoreCard(staffApplicationId)
    },
    enabled: !!staffApplicationId
  })

  const { data: howDidYouHearAboutUsOptions } = useQuery({
    queryKey: ['how-did-you-hear-about-us'],
    queryFn: () => getHowDidYouHearAboutUs()
  })
  const { data: pcrs } = useQuery({
    queryKey: ['pcrs'],
    queryFn: () => getPcrs({})
  })
  const deactivatedAt = form.watch('deactivatedAt')

  return (
    <div>
      {mode === 'update' && (
        <>
          {deactivatedAt && (
            <div className="mb-4">
              <Typography variant="label-semibold" color={tailwindColors.red.DEFAULT}>
                Désactivé le {DateFormatter.DDMMYYYY(new Date(deactivatedAt))}
              </Typography>
            </div>
          )}
          <FormField
            control={form.control}
            name="deactivationReason"
            disabled
            render={({ field }) => (
              <FormItem className="w-full mb-4">
                <FormControl>
                  <ControlledSelectInput
                    icon={(props) => (
                      <div className="bg-red-light p-1 rounded-[10px] mr-2">
                        <DeactivateFileIcon {...props} color={tailwindColors.red.DEFAULT} />
                      </div>
                    )}
                    {...field}
                    searchable
                    label="Motif de désactivation"
                    options={Object.values(DeactivationReasonEnum).map((status) => ({
                      label: deactivationReasonEnumMapping[status],
                      value: status
                    }))}
                    selectedOption={
                      field.value
                        ? {
                            label: deactivationReasonEnumMapping[field.value],
                            value: field.value
                          }
                        : undefined
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </>
      )}
      <div className="flex gap-3 items-center w-full">
        <FormField
          control={form.control}
          name="recruitedAt"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledDateStringInput {...field} label="Date de recrutement" />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="w-full">
          <FormField
            control={form.control}
            name="recruitedById"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      searchable
                      label="Recruteur"
                      options={pcrs?.data.map((option) => ({
                        label: `${toFullName(option.firstName, option.lastName)}`,
                        value: option.userId
                      }))}
                      selectedOption={getSelectedOption(
                        field.value,
                        `${toFullName(
                          pcrs?.data.find((option) => option.userId === field.value)?.firstName,
                          pcrs?.data.find((option) => option.userId === field.value)?.lastName
                        )}`
                      )}
                    />
                  </FormControl>
                </FormItem>
              )
            }}
          />
        </div>
      </div>
      <div className="h-4" />

      <div className="flex flex-col gap-1">
        <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
          Appréciation globale :
        </Typography>
        <div className="flex gap-20 mt-1">
          {Object.values(EvalutationEnum).map((evaluation) => (
            <FormField
              key={evaluation}
              control={form.control}
              name="globalEvaluation"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledRadioInput
                      {...field}
                      label={evalutationEnumMapping[evaluation].letter}
                      checked={field.value === evaluation}
                      onCheck={() => field.onChange(evaluation)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          ))}
        </div>
      </div>
      <div className="h-4" />

      <FormField
        control={form.control}
        name="howDidYouHearAboutUsOptionId"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <ControlledSelectInput
                {...field}
                searchable
                label="Comment avez-vous connu LEC ?"
                options={howDidYouHearAboutUsOptions?.data.map((option) => ({
                  label: option.name,
                  value: option.id
                }))}
                selectedOption={getSelectedOption(
                  field.value,
                  howDidYouHearAboutUsOptions?.data.find((option) => option.id === field.value)
                    ?.name || ''
                )}
              />
            </FormControl>
          </FormItem>
        )}
      />

      {mode === 'update' && (
        <>
          <div className="mt-4" />
          <div className="flex gap-3">
            <Button
              variant="ghost"
              type="button"
              size="medium"
              disabled={!staffApplicationId}
              icon={EyeOpenIcon}
              label="Voir la candidature"
              onClick={() => {
                if (staffApplicationId)
                  window.open(
                    Paths.UPDATE_STAFF_APPLICATION_PAGE.replace(
                      ':id',
                      staffApplicationId.toString()
                    )
                  )
              }}
            />

            <Button
              variant="ghost"
              type="button"
              size="medium"
              icon={NotebookIcon}
              disabled={!staffApplicationId}
              label="Voir le rapport d'entretien"
              onClick={() => toggleModal('staff-evaluation')}
            />

            {data && (
              <PcrScoreCardModal
                readOnly
                status={data.application.status}
                isOpen={modalState['staff-evaluation']}
                onCancel={() => closeModel('staff-evaluation')}
                staffApplication={data.application}
                defaultValues={mapScoreCardToInput(data)}
                isLoading={isPending}
                handleReset={() => closeModel('staff-evaluation')}
                hasViewButton={false}
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default StaffRecrutementForm
